import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { Box, Typography, CircularProgress } from "@mui/material";
import { getDefectReportSummary, updateDefectCount } from "../../api/qcReports";
import { themeBalham } from "ag-grid-community";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

export default function QCReportSheet({ props }) {
    const { id: reportHeaderId } = props;
    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const gridRef = useRef();

    const onGridReady = useCallback((params) => {
        // Remove fetchData call from here to prevent infinite loop
        // Data is already fetched in useEffect
    }, []);

    const onFirstDataRendered = useCallback((params) => {
        params.api.autoSizeAllColumns();
    }, []);

    const fetchData = useCallback(async () => {
        if (!reportHeaderId) return;

        setLoading(true);
        setError(null);
        try {
            const response = await getDefectReportSummary(reportHeaderId);
            setRowData(response.data || []);
        } catch (error) {
            console.error("Error fetching defect report summary:", error);
            setError("Error al cargar el reporte de defectos");
        } finally {
            setLoading(false);
        }
    }, [reportHeaderId]);

    const onCellValueChanged = useCallback(async (params) => {
        const { data, colDef, newValue } = params;
        if (!colDef.field.startsWith('hour')) return;

        const hourNumber = parseInt(colDef.field.replace('hour', ''));
        
        try {
            setLoading(true);
            await updateDefectCount({
                reportHeaderId: reportHeaderId,
                defectId: data.defectId,
                hour: hourNumber,
                noDefects: newValue
            });
            await fetchData(); // Refresh data after update
        } catch (error) {
            console.error('Error updating defect count:', error);
            setError('Error al actualizar el conteo de defectos');
            await fetchData(); // Refresh to revert changes on error
        } finally {
            setLoading(false);
        }
    }, [reportHeaderId, fetchData]);

    useEffect(() => {
        if (reportHeaderId) {
            fetchData();
        }
    }, [reportHeaderId, fetchData]);

    const getHourColDef = useCallback((hour) => ({
        field: `hour${hour}`,
        headerName: `H${hour}`,
        width: 15,
        type: "numericColumn",
        editable: true,
        cellStyle: { cursor: 'pointer' },
        valueParser: (params) => {
            const value = Number(params.newValue);
            return isNaN(value) ? null : value;
        }
    }), []);

    const columnDefs = useMemo(() => [
        {
            field: "controlName",
            headerName: "Control",
            flex: 1,
            minWidth: 100,
            pinned: "left",
            editable: false,
        },
        {
            field: "defectName",
            headerName: "Defecto",
            flex: 1,
            minWidth: 50,
            pinned: "left",
            editable: false,
        },
        getHourColDef(1),
        getHourColDef(2),
        getHourColDef(3),
        getHourColDef(4),
        getHourColDef(5),
        getHourColDef(6),
        getHourColDef(7),
        getHourColDef(8),
        getHourColDef(9),
        getHourColDef(10),
        getHourColDef(11),
        getHourColDef(12),
        getHourColDef(13),
        getHourColDef(14),
        getHourColDef(15),
        getHourColDef(16),
    ], [getHourColDef]);

    const defaultColDef = useMemo(() => ({
        resizable: true,
        autoHeight: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        headerClass: 'wrap-text-header'
    }), []);

    // Add custom CSS for header wrapping
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
      .ag-cell {
        min-width: 15px !important;
      }
      .wrap-text-header .ag-header-cell-label {
        white-space: normal !important;
        line-height: 1.2;
      }
      .wrap-text-header .ag-header-cell-label span {
        white-space: normal !important;
        word-break: break-word;
      }
      .wrap-text-header .ag-header-cell {
        padding: 4px !important;
      }
      .ag-header-cell {
        line-height: 1.2;
      }
      .ag-header-cell-text {
        overflow: visible !important;
      }
      .ag-cell-focus {
        border: 1px solid #2196f3 !important;
      }
    `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box width="100%">
            <Typography variant="h6" gutterBottom>
                Resumen de Defectos por Hora
            </Typography>
            <div
                style={{
                    height: 500,
                    width: "100%",
                }}
            >
                <AgGridReact
                    theme={themeBalham}
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={[5, 10, 20, 50]}
                    animateRows={true}
                    loading={loading}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                    onCellValueChanged={onCellValueChanged}
                    suppressColumnVirtualisation={true}
                    headerHeight={null}
                />
            </div>
        </Box>
    );
} 